import { ssrBannerManager } from '@confluence/ssr-utilities/entry-points/SSRBannerManager.ts';
import type { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';

import { useGetCurrentStorageUsage } from '../hooks/useGetCurrentStorageUsage.ts';
import { CONFLUENCE_STORAGE_LIMITS, WARNING_THRESHOLD } from '../storageLimits/storageLimits.ts';

import { BANNER_HEIGHT, BANNER_NAME } from './constants.ts';
import { useStorageEnforcementDismissalState } from './useStorageEnforcementBannerDismissalState.ts';

export function useRegisterStorageEnforcementBanner(
	bannerState: BannerStateContainer | null = null,
) {
	const { currentUsage, loading, error, gracePeriodEndDate } = useGetCurrentStorageUsage();

	const shouldRenderHard = currentUsage >= CONFLUENCE_STORAGE_LIMITS.FREE && gracePeriodEndDate;
	let shouldRenderSoft =
		currentUsage >= CONFLUENCE_STORAGE_LIMITS.FREE * WARNING_THRESHOLD &&
		currentUsage < CONFLUENCE_STORAGE_LIMITS.FREE;

	const { isDismissed, onDismiss } = useStorageEnforcementDismissalState({
		currentUsage,
		skip: !shouldRenderSoft,
	});

	shouldRenderSoft &&= !isDismissed; // If they havent dismissed the warning banner -> should see it

	const shouldRender = Boolean(shouldRenderHard || shouldRenderSoft);
	if (shouldRender) {
		bannerState?.show(BANNER_NAME, BANNER_HEIGHT);
		addUFOCustomData({
			isStorageEnforcementBannerDisplayed: true,
		});
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			ssrBannerManager.registerBanner({
				name: BANNER_NAME,
				height: BANNER_HEIGHT,
			});
		}
	} else {
		bannerState?.hide(BANNER_NAME);
	}

	return {
		shouldRender, // Controls whether or not we abort and render nothing.
		shouldRenderHard,
		currentUsage,
		onDismiss,
		error,
		loading,
	};
}
